import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import ogImageAsset from "../assets/og-tb-logo-navy.png.asset.json";
const ogImage = `https://tablesandbridges.com${ogImageAsset.url}`;
import { reportLovableError } from "../lib/lovable-error-reporting";
import { initSentry } from "../lib/sentry-init";

initSentry();

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Tables and Bridges | Community Engagement for Law Enforcement" },
      { name: "description", content: "The professional home for law enforcement Community Engagement professionals and agencies: methodology, training, practitioner community, and podcast." },
      { property: "og:title", content: "Tables and Bridges | Community Engagement for Law Enforcement" },
      { property: "og:description", content: "The professional home for law enforcement Community Engagement professionals and agencies: methodology, training, practitioner community, and podcast." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Tables and Bridges" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Tables and Bridges | Community Engagement for Law Enforcement" },
      { name: "twitter:description", content: "The professional home for law enforcement Community Engagement professionals and agencies: methodology, training, practitioner community, and podcast." },
      { property: "og:image", content: ogImage },
      { property: "og:image:alt", content: "Tables and Bridges logo - a navy and green circular emblem of a table framed by a bridge arch, above the wordmark Tables and Bridges and the tagline: Building Trust. Connecting Communities. Creating Impact." },
      { name: "twitter:image", content: ogImage },
      { name: "twitter:image:alt", content: "Tables and Bridges logo - a navy and green circular emblem of a table framed by a bridge arch, above the wordmark Tables and Bridges and the tagline: Building Trust. Connecting Communities. Creating Impact." },
      { name: "robots", content: "index, follow" },
      { name: "google-site-verification", content: "6cFxkwFCgT3nGEVYXfLtnMo1AQJiuE7e3Apf63_TgM0" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&display=swap",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "Tables and Bridges",
              url: "https://tablesandbridges.com",
              logo: "https://storage.googleapis.com/gpt-engineer-file-uploads/jBjelmXX89VF5xyFHBB2jubW0sg2/social-images/social-1782028967879-logos_(4).webp",
            },
            {
              "@type": "WebSite",
              name: "Tables and Bridges",
              url: "https://tablesandbridges.com",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

import { NavBar } from "@/components/navbar";
import { Footer } from "@/components/footer";

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-dvh flex-col">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-gold focus:px-4 focus:py-3 focus:text-sm focus:font-semibold focus:text-navy"
        >
          Skip to main content
        </a>
        <NavBar />
        <main id="main" tabIndex={-1} className="flex-1">

          {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
          <Outlet />
        </main>
        <Footer />
      </div>
    </QueryClientProvider>
  );
}
